import { docs } from '@/services/docs/docs-service'
import { CircularProgress } from '@heroui/react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts'

interface DocumentsMetrics {
  documentTypes: {
    [key: string]: number
  }
  issueTypes: {
    [key: string]: number
  }
  issueTypesGeneric: {
    [key: string]: number
  }
  versionsCount: number
  uniqueDocumentsCount: number
  genericDocumentsCount: number
  risks: {
    average: number
    max: number
  }
  pages: {
    average: number
    max: number
  }
  versionsPerDocument: {
    average: number
    max: number
  }
  reviews: {
    chartData: { date: string; count: number }[]
    pastWeek: number
    pastMonth: number
    total: number
    ongoing: number
  }
  sends: {
    chartData: { date: string; count: number }[]
    pastWeek: number
    pastMonth: number
    pastThreeMonths: number
    total: number
  }
}

export default function DocumentsMetricsPage() {
  const [documentsMetrics, setDocumentsMetrics] = useState<Partial<DocumentsMetrics>>({})
  const [loadingStates, setLoadingStates] = useState({
    basic: true,
    stats: true,
    issues: true,
    reviews: true,
    sends: true,
  })
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Helper to fetch with retry logic
  const fetchWithRetry = async (
    type: 'basic' | 'stats' | 'issues' | 'reviews' | 'sends',
    retryCount = 0
  ): Promise<any> => {
    try {
      return await docs.getDocumentsMetricsByType(type)
    } catch (error) {
      if (retryCount < 1) {
        // Retry once
        console.log(`Retrying ${type} metrics...`)
        return await fetchWithRetry(type, retryCount + 1)
      }
      throw error
    }
  }

  const fetchMetricsByType = (type: 'basic' | 'stats' | 'issues' | 'reviews' | 'sends') => {
    setLoadingStates(prev => ({ ...prev, [type]: true }))

    fetchWithRetry(type)
      .then(data => {
        setDocumentsMetrics(prev => ({ ...prev, ...data }))
      })
      .catch(() => {
        toast.error(`Failed to fetch ${type} metrics`)
      })
      .finally(() => {
        setLoadingStates(prev => ({ ...prev, [type]: false }))
      })
  }

  const fetchMetrics = (showRefreshIndicator = false) => {
    if (showRefreshIndicator) {
      setIsRefreshing(true)
      setDocumentsMetrics({})
    }

    // Stagger requests by 150ms each to avoid overwhelming DB
    const types: Array<'basic' | 'stats' | 'issues' | 'reviews' | 'sends'> = [
      'basic',
      'stats',
      'issues',
      'reviews',
      'sends',
    ]

    types.forEach((type, index) => {
      setTimeout(() => {
        fetchMetricsByType(type)
      }, index * 1000)
    })

    // Wait for all to complete for refresh indicator
    if (showRefreshIndicator) {
      const checkComplete = setInterval(() => {
        setLoadingStates(prev => {
          const allDone = Object.values(prev).every(state => !state)
          if (allDone) {
            clearInterval(checkComplete)
            setIsRefreshing(false)
            toast.success('Done', { duration: 1000 })
          }
          return prev
        })
      }, 100)
    }
  }

  useEffect(() => {
    fetchMetrics()
  }, [])

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold">Documents Metrics</h1>
        <p className="text-muted-foreground">
          What insights have we got for our last {documentsMetrics.versionsCount || '150'} reviewed versions?
        </p>
      </div>

      {/* Fixed Refresh Button */}
      <button
        onClick={() => fetchMetrics(true)}
        disabled={isRefreshing}
        className="fixed bottom-8 right-8 z-50 flex items-center gap-2 rounded-full bg-primary px-4 py-3 text-primary-foreground shadow-lg transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        title="Refresh metrics"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={isRefreshing ? 'animate-spin' : ''}
        >
          <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
        </svg>
        <span className="text-sm font-medium">Refresh</span>
      </button>

      <div className="space-y-8">
        {/* Basic Metrics Section */}
        {loadingStates.basic ? (
          <div className="grid w-full place-items-center p-4 py-6">
            <CircularProgress color="success" label="Loading basic metrics..." size="sm" />
          </div>
        ) : documentsMetrics.documentTypes ? (
          <div className="grid grid-cols-[20%_1fr] gap-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                <h3 className="text-sm font-medium text-muted-foreground">Total Versions</h3>
                <p className="mt-2 text-3xl font-bold">{documentsMetrics.versionsCount}</p>
              </div>
              <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                <h3 className="text-sm font-medium text-muted-foreground">Unique Documents</h3>
                <p className="mt-2 text-3xl font-bold">{documentsMetrics.uniqueDocumentsCount}</p>
              </div>
              <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                <h3 className="text-sm font-medium text-muted-foreground">Generic Documents</h3>
                <p className="mt-2 text-3xl font-bold">{documentsMetrics.genericDocumentsCount}</p>
              </div>
            </div>
            {/* Document Types Chart */}
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <h2 className="mb-4 text-xl font-semibold">Document Types Distribution</h2>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={Object.entries(documentsMetrics.documentTypes).map(([name, value]) => ({
                      name: name.charAt(0).toUpperCase() + name.slice(1),
                      value,
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(entry: any) => `${entry.name}: ${((entry.percent || 0) * 100).toFixed(0)}%`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {Object.keys(documentsMetrics.documentTypes).map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : null}

        {/* Stats Metrics Section */}
        {loadingStates.stats ? (
          <div className="grid w-full place-items-center p-4 py-6">
            <CircularProgress color="success" label="Loading stats metrics..." size="sm" />
          </div>
        ) : documentsMetrics.risks ? (
          <div className="grid grid-cols-3 gap-4">
            {(['risks', 'pages', 'versionsPerDocument'] as const).map(e => (
              <div key={e} className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">{e.charAt(0).toUpperCase() + e.slice(1)}</h2>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={[
                      { metric: e, Average: documentsMetrics[e]?.average || 0, Max: documentsMetrics[e]?.max || 0 },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="metric" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Average" fill="#8b5cf6" />
                    <Bar dataKey="Max" fill="#ec4899" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        ) : null}

        {/* Issues Metrics Section */}
        {loadingStates.issues ? (
          <div className="grid w-full place-items-center p-4 py-6">
            <CircularProgress color="success" label="Loading issues metrics..." size="sm" />
          </div>
        ) : documentsMetrics.issueTypes ? (
          <>
            {/* All Versions */}
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <h2 className="mb-4 text-xl font-semibold">Issue Types - All Versions (Top 10)</h2>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={Object.entries(documentsMetrics.issueTypes).map(([name, count]) => ({
                    name,
                    count,
                  }))}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="count" fill="#10b981" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Generic Documents Only */}
            {documentsMetrics.issueTypesGeneric && (
              <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
                <h2 className="mb-4 text-xl font-semibold">Issue Types - Generic Documents (Top 10)</h2>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart
                    data={Object.entries(documentsMetrics.issueTypesGeneric).map(([name, count]) => ({
                      name,
                      count,
                    }))}
                    margin={{ top: 20, right: 20, left: 0, bottom: 50 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#ec4899" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        ) : null}

        {/* Reviews Metrics Section */}
        {loadingStates.reviews ? (
          <div className="grid w-full place-items-center p-4 py-6">
            <CircularProgress color="success" label="Loading reviews metrics..." size="sm" />
          </div>
        ) : documentsMetrics.reviews ? (
          <div className="grid grid-cols-[80%_1fr] gap-4">
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <h2 className="mb-4 text-xl font-semibold">Reviews Initiated (Last 15 Days)</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={documentsMetrics.reviews.chartData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line dataKey="count" stroke="#3b82f6" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <ul className="flex flex-col gap-4">
              {[
                {
                  title: 'Ongoing Reviews',
                  value: documentsMetrics.reviews.ongoing,
                  color: 'text-blue-500',
                },
                { title: 'Past Week', value: documentsMetrics.reviews.pastWeek },
                { title: 'Past Month', value: documentsMetrics.reviews.pastMonth },
                { title: 'Total Reviews', value: documentsMetrics.reviews.total },
              ].map(card => (
                <li key={card.title} className="grow rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium text-muted-foreground">{card.title}</h3>
                  <p className={`mt-2 text-3xl font-bold ${card.color || ''}`}>{card.value}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Sends Metrics Section */}
        {loadingStates.sends ? (
          <div className="grid w-full place-items-center p-4 py-6">
            <CircularProgress color="success" label="Loading sends metrics..." size="sm" />
          </div>
        ) : documentsMetrics.sends ? (
          <div className="grid grid-cols-[80%_1fr] gap-4">
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <h2 className="mb-4 text-xl font-semibold">Document Sends (Last 15 Days)</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={documentsMetrics.sends.chartData} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" angle={-45} textAnchor="end" height={50} />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Line dataKey="count" stroke="#10b981" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <ul className="flex flex-col gap-4">
              {[
                { title: 'Past Week', value: documentsMetrics.sends.pastWeek },
                { title: 'Past Month', value: documentsMetrics.sends.pastMonth },
                { title: 'Past 3 Months', value: documentsMetrics.sends.pastThreeMonths },
                { title: 'Total Sends', value: documentsMetrics.sends.total },
              ].map(card => (
                <li key={card.title} className="grow rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
                  <h3 className="text-sm font-medium text-muted-foreground">{card.title}</h3>
                  <p className="mt-2 text-3xl font-bold">{card.value}</p>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </>
  )
}

const COLORS = ['#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#3b82f6', '#ef4444', '#14b8a6', '#f97316']
